import { FC } from "react";
import { useAppTranslation } from "@/utils";
import { URLS } from "@payping/utilities-shared/consts";
import { buildUrl } from "@payping/utilities-shared/url";
import { Button, Divider, Drawer, Flex, Typography } from "@payping/web-components";
import { dayjs, formatDayjsToFullPersian } from "@payping/utilities-shared/date";
import { formatLocalizedNumber, numberToPersianWords } from "@payping/utilities-shared/number";

interface ItemProps {
  title: string;
  content: string;
  number: number;
}
const Item: FC<ItemProps> = ({ content, number, title }) => {
  return (
    <Flex align="start" className="tw:mb-2! tw:w-full" gap={"small"}>
      <Flex
        justify="center"
        align="center"
        className="text--white tw:h-4.5 tw:w-4.5 tw:rounded-full tw:bg-color-text-tertiary"
      >
        {number}
      </Flex>
      <Flex vertical gap={"4px"}>
        <Typography.Text type="secondary" className="tw:m-0">
          {title}
        </Typography.Text>
        <Typography.Text className="tw:m-0">{content}</Typography.Text>
      </Flex>
    </Flex>
  );
};
interface HowToWriteProps {
  open: boolean;
  onClose: () => void;
  data: {
    amountRial?: number;
    duedate?: string;
    recipientName?: string;
    recipientIdentifier?: string;
    isLegal: boolean;
  };
}

const HowToWriteChequeDrawer: FC<HowToWriteProps> = ({ onClose, open, data }) => {
  const { t } = useAppTranslation();

  if (!open) return null;
  const AmountRial = () => {
    try {
      const result = numberToPersianWords(data?.amountRial ?? 0);
      if (typeof result === "string") {
        return result;
      }
      return null;
    } catch {
      return null;
    }
  };

  return (
    <Drawer
      title={t("howToWriteCheckDrawer.title")}
      size={"95%"}
      getContainer={false}
      placement="bottom"
      onClose={onClose}
      open={open}
    >
      <Flex className="tw:min-h-full tw:w-full" vertical gap={"large"}>
        <img
          src={buildUrl(URLS.STORAGE_URL, "bnpl-consumer/how-to-write-a-cheque.png").toString()}
          className="tw:h-auto tw:w-full tw:object-contain"
          alt={"how-to-write-a-cheque"}
        />
        <Typography.Text>{t("howToWriteCheckDrawer.form.title")}</Typography.Text>
        <Flex vertical className="tw:w-full">
          <Item number={1} title={t("howToWriteCheckDrawer.form.dateToNumber")} content={data?.duedate ?? ""} />
          <Divider className="tw:m-0! tw:mb-2!" />
          <Item
            number={2}
            title={t("howToWriteCheckDrawer.form.dateToWords")}
            content={formatDayjsToFullPersian(dayjs(data?.duedate, { jalali: true } as unknown as any))}
          />
          <Divider className="tw:m-0! tw:mb-2!" />
          <Item
            number={3}
            title={t("howToWriteCheckDrawer.form.amountToNumber")}
            content={formatLocalizedNumber({ value: data?.amountRial ?? 0 })}
          />
          <Divider className="tw:m-0! tw:mb-2!" />
          <Item number={4} title={t("howToWriteCheckDrawer.form.amountToWords")} content={AmountRial() ?? ""} />
          <Divider className="tw:m-0! tw:mb-2!" />
          <Item number={5} title={t("howToWriteCheckDrawer.form.inTheName")} content={data?.recipientName ?? ""} />
          <Divider className="tw:m-0! tw:mb-2!" />
          <Item
            number={6}
            title={
              data.isLegal ? t("howToWriteCheckDrawer.form.nationalId") : t("howToWriteCheckDrawer.form.nationalCode")
            }
            content={data?.recipientIdentifier ?? ""}
          />
        </Flex>
        <Button block type="primary" className="tw:mt-auto" onClick={onClose}>
          {t("common.confirmed")}
        </Button>
      </Flex>
    </Drawer>
  );
};

export default HowToWriteChequeDrawer;
